import { useEffect } from 'react'

// The sticky stack: each card recedes (scales down a little) as the next one
// slides over it. Progress per card runs from 0 the moment it sticks to 1
// once the next card has stuck on top of it. Raw progress moves in lockstep
// with the finger/wheel, which reads as mechanical, so the scale eases toward
// it a little each frame instead of snapping (the feel the framer-motion
// spring used to give).
//
// Opacity is deliberately NOT part of that recede -- a translucent card lets
// its own text show through the opaque card stacked on top of it, which
// reads as double-exposed, ghosted text rather than depth. The card has to
// stay fully solid for the next one to actually cover it; scale is the only
// cue that it is receding. It is written to the `scale` property, so it never
// fights the card's own transform or its entrance (`translate`).
const RECEDE_TO = 0.93
const EASE_PER_FRAME = 0.18

// Used by ShowcaseStack and by the client cards (ClientCards.jsx), which only
// stack on wide screens: while the cards are not sticky, nothing recedes.
export function useStackRecede(listRef, items) {
  useEffect(() => {
    const list = listRef.current
    if (!list || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined

    const cards = Array.from(list.children)
    const current = cards.map(() => 0)
    let ranges = []
    let frame = 0

    // Positions as if nothing were sticky (a stuck card's own rect follows
    // the viewport), relative to the list: every earlier card's height and
    // margin, less each card's sticky `top`. Relative, so content loading
    // above the list (which moves it) needs no re-measure; the list's own
    // position is read once per frame instead.
    const measure = () => {
      if (cards.length && getComputedStyle(cards[0]).position !== 'sticky') {
        ranges = []
        cards.forEach((card, i) => {
          current[i] = 0
          card.style.scale = ''
        })
        return
      }
      let y = 0
      const tops = cards.map((card) => {
        const top = y
        y += card.offsetHeight + (parseFloat(getComputedStyle(card).marginBottom) || 0)
        return top
      })
      const stuckAt = cards.map((card, i) => tops[i] - (parseFloat(getComputedStyle(card).top) || 0))
      ranges = cards.map((card, i) => {
        const end = i + 1 < cards.length ? stuckAt[i + 1] : stuckAt[i] + card.offsetHeight
        return { from: stuckAt[i], span: Math.max(1, end - stuckAt[i]) }
      })
    }

    const tick = () => {
      frame = 0
      if (!ranges.length) return
      // How far the page has scrolled past the list's top. Read before any
      // writes, so it never forces a layout.
      const scrolled = -list.getBoundingClientRect().top
      let settling = false
      cards.forEach((card, i) => {
        const { from, span } = ranges[i]
        const target = Math.min(1, Math.max(0, (scrolled - from) / span))
        const next = current[i] + (target - current[i]) * EASE_PER_FRAME
        current[i] = Math.abs(target - next) < 0.001 ? target : next
        if (current[i] !== target) settling = true
        card.style.scale = String(1 - (1 - RECEDE_TO) * current[i])
      })
      if (settling) frame = requestAnimationFrame(tick)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(tick)
    }
    const remeasure = () => {
      measure()
      schedule()
    }

    // No measuring here: reading sizes during hydration would force a layout
    // of the whole page. ResizeObserver reports once right after the next
    // layout (and again on any size change), when the reads are free.
    window.addEventListener('scroll', schedule, { passive: true })
    const resize = new ResizeObserver(remeasure)
    resize.observe(list)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      resize.disconnect()
      cards.forEach((card) => {
        card.style.scale = ''
      })
    }
  }, [listRef, items])
}
